import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';

import {
  Dialog, DialogContent, DialogActions, Button, Typography, IconButton,
  Stack, Chip, CircularProgress, Box, Paper, Popper, ClickAwayListener, MenuItem, Divider,
} from '@mui/material';
import {
  Close, Add, EditOutlined, RestartAltOutlined,
  AutoAwesome, PeopleAltOutlined, ArrowForward,
} from '@mui/icons-material';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, useSortable, horizontalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useQueryClient } from '@tanstack/react-query';

import { AlertMessage } from 'utilities/utils';
import { useTrackEvent } from 'analytics/useTrackEvent';
import { useGetApiV5FilesNamingConfig } from '@liscio/api';
import {
  useGetApiV5FirmsFileNamingTemplates,
  usePutApiV5FirmsFileNamingTemplatesId,
  getGetApiV5FirmsFileNamingTemplatesQueryKey,
} from '@liscio/api';

import { COLORS } from 'components/AI/DeterministicFileNameManagement/constants';
import { splitFilename } from 'components/AI/DeterministicFileNameManagement/utils/filename';

import {
  TemplateToken, VariableMeta, FormatHintMeta,
  VARIABLE_ORDER, SUPPRESSED_VARIABLES, FIRM_DEFAULT_TYPE,
  SEPARATOR_OPTIONS, DEFAULT_SEPARATOR,
  serializeTokens, parseTemplateString, buildPreview, createToken,
} from './types';

interface TemplateConfigModalProps {
  open: boolean;
  onClose: () => void;
  /** Upload filename for the before/after preview; extension carries onto the suggested name. */
  originalName?: string;
  previewValues?: Record<string, string> | null;
  onTemplateSaved?: () => void;
  /** Which flow opened this modal — used to tag analytics. */
  source?: string;
}

// Fallback when the modal opens without a file context (e.g. isolated tests).
const FALLBACK_ORIGINAL_FILENAME = 'Generated image 1.pdf';

interface DetectedField {
  variable: string;
  label: string;
  value: string;
}

/**
 * The annotated "Fields Liscio found" values — one per variable used in the
 * active pattern, resolved from `previewValues` and labelled with the human
 * label from the naming config (never the internal variable name when a label
 * exists). Deduped by variable; tokens without a resolved value are skipped.
 */
const getDetectedFields = (
  tokens: TemplateToken[],
  allVars: Record<string, VariableMeta>,
  previewValues?: Record<string, string> | null,
): DetectedField[] => {
  const seen = new Set<string>();
  const fields: DetectedField[] = [];
  tokens.forEach((t) => {
    if (seen.has(t.variable)) return;
    const key = `${t.variable}${t.hint ? `:${t.hint}` : ''}`;
    const value = previewValues?.[key] ?? previewValues?.[t.variable];
    if (value == null || value === '') return;
    seen.add(t.variable);
    fields.push({ variable: t.variable, label: allVars[t.variable]?.label || t.variable, value });
  });
  return fields;
};

const getChipLabel = (
  token: TemplateToken,
  allVars: Record<string, VariableMeta>,
  hints: Record<string, Record<string, FormatHintMeta>>,
): string => {
  const varLabel = allVars[token.variable]?.label || token.variable;
  if (!token.hint) return varLabel;
  const hintMeta = hints[token.variable]?.[token.hint];
  return `${varLabel} (${hintMeta?.description || token.hint})`;
};

const getEditChipLabel = (
  token: TemplateToken,
  allVars: Record<string, VariableMeta>,
  hints: Record<string, Record<string, FormatHintMeta>>,
): string => {
  const varLabel = allVars[token.variable]?.label || token.variable;
  const hasHints = hints[token.variable] && Object.keys(hints[token.variable]).length > 0;
  const suffix = hasHints ? ' ▾' : '';
  if (!token.hint) return `${varLabel}${suffix}`;
  const hintMeta = hints[token.variable]?.[token.hint];
  return `${varLabel} (${hintMeta?.description || token.hint})${suffix}`;
};

const SortableChip = ({
  id, label, onClickChip, onRemove,
}: {
  id: string;
  label: string;
  onClickChip: (e: React.MouseEvent<HTMLElement>) => void;
  onRemove: () => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style} sx={{ position: 'relative', '&:hover .chip-remove': { opacity: 1 } }}>
      <Chip
        label={label}
        onClick={onClickChip}
        variant="outlined"
        sx={{
          height: 30, fontSize: '13px', fontWeight: 500,
          borderColor: isDragging ? COLORS.drag.border : COLORS.chip.view.border, borderRadius: '6px',
          color: COLORS.chip.view.text, backgroundColor: isDragging ? COLORS.drag.bg : COLORS.chip.view.bg,
          cursor: 'grab', boxShadow: isDragging ? COLORS.drag.shadow : 'none',
          transition: 'box-shadow 0.15s ease, background-color 0.15s ease',
        }}
        {...attributes}
        {...listeners}
      />
      <IconButton
        className="chip-remove"
        size="small"
        aria-label="Remove"
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        sx={{
          position: 'absolute', top: -6, right: -6, width: 16, height: 16,
          p: 0, backgroundColor: COLORS.remove.bg, color: '#fff',
          opacity: 0, transition: 'opacity 0.15s ease',
          '&:hover': { backgroundColor: COLORS.remove.bgHover },
        }}
      >
        <Close sx={{ fontSize: '10px' }} />
      </IconButton>
    </Box>
  );
};

const SeparatorPill = ({ value, onClick }: {
  value: string;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
}) => {
  const option = SEPARATOR_OPTIONS.find((o) => o.value === value);
  const display = option?.display.trim() ? option.display : ' ';
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={`Separator: ${option?.label ?? 'custom'}`}
      sx={{
        background: 'none', border: `1px dashed ${COLORS.separator.edit.border}`, borderRadius: '4px',
        cursor: 'pointer', px: 0.3125, py: 0, minWidth: 20, height: 24,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: COLORS.separator.edit.text, fontSize: '12px', fontFamily: 'monospace', fontWeight: 500,
        '&:hover': { borderColor: COLORS.separator.edit.hoverBorder, backgroundColor: COLORS.separator.edit.hoverBg },
      }}
    >
      {display}
    </Box>
  );
};

const SeparatorGlyph = ({ value }: { value: string }) => {
  const option = SEPARATOR_OPTIONS.find((o) => o.value === value);
  const display = option?.display.trim() ? option.display : ' ';
  return (
    <Box
      role="img"
      aria-label={`Separator: ${option?.label ?? 'custom'}`}
      sx={{
        border: `1px dashed ${COLORS.separator.view.border}`, borderRadius: '4px',
        px: 0.3125, py: 0, minWidth: 20, height: 24,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: COLORS.separator.view.text, fontSize: '12px', fontFamily: 'monospace', fontWeight: 500,
      }}
    >
      {display}
    </Box>
  );
};

export const TemplateConfigModal = ({ open, onClose, originalName, previewValues, onTemplateSaved, source = 'file_intelligence' }: TemplateConfigModalProps) => {
  const queryClient = useQueryClient();
  const trackEvent = useTrackEvent();

  // ----- Data fetching -----
  const { data: configData, isLoading: configLoading } = useGetApiV5FilesNamingConfig({
    query: { enabled: open },
  });
  const { data: templatesData, isLoading: templatesLoading } = useGetApiV5FirmsFileNamingTemplates({
    query: { enabled: open },
  });
  const { mutateAsync: saveTemplate, isLoading: saving } = usePutApiV5FirmsFileNamingTemplatesId();

  const loading = configLoading || templatesLoading;

  const allVars: Record<string, VariableMeta> = configData
    ? { ...(configData.variables?.insight as Record<string, VariableMeta> || {}),
        ...(configData.variables?.db as Record<string, VariableMeta> || {}) }
    : {};
  const formatHints: Record<string, Record<string, FormatHintMeta>> =
    (configData?.format_hints as Record<string, Record<string, FormatHintMeta>>) || {};

  const firmDefault = (templatesData?.templates as Array<{
    document_type: string; template_string: string | null; is_firm_override: boolean;
  }>)?.find((t) => t.document_type === FIRM_DEFAULT_TYPE);
  const currentTemplate = firmDefault?.template_string || null;
  const currentParsed = useMemo(
    () => currentTemplate ? parseTemplateString(currentTemplate) : { tokens: [], separators: [], prefix: '', suffix: '' },
    [currentTemplate],
  );

  // ----- State -----
  const [isEditing, setIsEditing] = useState(false);
  const [tokens, setTokens] = useState<TemplateToken[]>([]);
  const [separators, setSeparators] = useState<string[]>([]);
  const [isDirty, setIsDirty] = useState(false);

  // Format hint popover
  const [popoverVar, setPopoverVar] = useState<string | null>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [editingTokenIndex, setEditingTokenIndex] = useState<number | null>(null);

  // Separator popover
  const [sepPopoverIndex, setSepPopoverIndex] = useState<number | null>(null);
  const [sepPopoverAnchor, setSepPopoverAnchor] = useState<HTMLElement | null>(null);

  // Drag-to-reorder
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const tokenIds = tokens.map((t) => t.id);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = tokenIds.indexOf(active.id as string);
    const newIndex = tokenIds.indexOf(over.id as string);
    if (oldIndex < 0 || newIndex < 0) return;
    // Only reorder tokens; separators stay at gap positions
    setTokens((prev) => arrayMove(prev, oldIndex, newIndex));
    setIsDirty(true);
  };

  // Initialize when modal opens and data is ready.
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      setIsDirty(false);
      return;
    }
    if (loading || initializedRef.current) return;
    if (isDirty) return;

    initializedRef.current = true;
    setIsEditing(!currentTemplate);
    setTokens(currentParsed.tokens);
    setSeparators(currentParsed.separators);
    setIsDirty(false);
    setPopoverVar(null);
    setPopoverAnchor(null);
    setEditingTokenIndex(null);
    setSepPopoverIndex(null);
    setSepPopoverAnchor(null);
  }, [open, loading, currentTemplate, isDirty]); // eslint-disable-line react-hooks/exhaustive-deps

  const enterEditMode = () => {
    setTokens(currentParsed.tokens);
    setSeparators(currentParsed.separators);
    setIsDirty(false);
    setIsEditing(true);
  };

  const activeTokens = isEditing ? tokens : currentParsed.tokens;
  const activeSeparators = isEditing ? separators : currentParsed.separators;
  const namePreview = buildPreview(
    activeTokens,
    activeSeparators,
    previewValues,
    currentParsed.prefix,
    currentParsed.suffix,
  );
  const originalFileName = originalName || FALLBACK_ORIGINAL_FILENAME;
  // Carry the original upload's extension onto the suggested name.
  const { ext: originalExt } = splitFilename(originalFileName);
  const newFileName = namePreview ? `${namePreview}${originalExt}` : '';
  const detectedFields = getDetectedFields(activeTokens, allVars, previewValues);

  const usedVars = new Set(tokens.map((t) => t.variable));
  const orderedVars = VARIABLE_ORDER.filter((v) => v in allVars && !usedVars.has(v));
  const unlistedVars = Object.keys(allVars).filter((v) => !VARIABLE_ORDER.includes(v) && !SUPPRESSED_VARIABLES.has(v) && !usedVars.has(v)).sort();
  const availableVars = [...orderedVars, ...unlistedVars];

  // ----- Token operations -----
  const addTokenToEnd = (variable: string, hint?: string) => {
    if (tokens.length > 0) {
      setSeparators((s) => [...s, DEFAULT_SEPARATOR]);
    }
    setTokens((prev) => [...prev, createToken(variable, hint)]);
    setIsDirty(true);
  };

  const removeToken = (index: number) => {
    setTokens((prev) => prev.filter((_, i) => i !== index));
    setSeparators((prev) => {
      if (prev.length === 0) return prev;
      // Remove the separator associated with this gap
      if (index === 0) return prev.slice(1);
      return [...prev.slice(0, index - 1), ...prev.slice(index)];
    });
    setIsDirty(true);
  };

  // ----- Format hint popover -----
  const closePopover = () => {
    setPopoverVar(null);
    setPopoverAnchor(null);
    setEditingTokenIndex(null);
  };

  const closeSepPopover = () => {
    setSepPopoverIndex(null);
    setSepPopoverAnchor(null);
  };

  const closeAllPopovers = () => {
    closePopover();
    closeSepPopover();
  };

  const openPopoverForAdd = (varName: string, el: HTMLElement) => {
    closeSepPopover(); // mutual exclusion
    const hints = formatHints[varName];
    if (!hints || Object.keys(hints).length === 0) {
      addTokenToEnd(varName);
    } else {
      setEditingTokenIndex(null);
      setPopoverVar(varName);
      setPopoverAnchor(el);
    }
  };

  const openPopoverForEdit = (index: number, el: HTMLElement) => {
    closeSepPopover(); // mutual exclusion
    const token = tokens[index];
    const hints = formatHints[token.variable];
    if (!hints || Object.keys(hints).length === 0) return;
    setEditingTokenIndex(index);
    setPopoverVar(token.variable);
    setPopoverAnchor(el);
  };

  const selectHint = (hint?: string) => {
    if (editingTokenIndex !== null) {
      setTokens((prev) => prev.map((t, i) => (i === editingTokenIndex ? { ...t, hint } : t)));
    } else if (popoverVar) {
      addTokenToEnd(popoverVar, hint);
    }
    setIsDirty(true);
    closePopover();
  };

  // ----- Separator popover -----
  const openSepPopover = (index: number, el: HTMLElement) => {
    closePopover(); // mutual exclusion
    setSepPopoverIndex(index);
    setSepPopoverAnchor(el);
  };

  const selectSeparator = (value: string) => {
    if (sepPopoverIndex !== null) {
      setSeparators((prev) => prev.map((s, i) => (i === sepPopoverIndex ? value : s)));
      setIsDirty(true);
    }
    closeSepPopover();
  };

  // ----- Save -----
  const handleSave = useCallback(async () => {
    const toSave = serializeTokens(
      tokens,
      separators,
      currentParsed.prefix,
      currentParsed.suffix,
    );
    if (!tokens.length) return;
    try {
      await saveTemplate({ id: FIRM_DEFAULT_TYPE, data: { template_string: toSave } });
      await queryClient.invalidateQueries({ queryKey: getGetApiV5FirmsFileNamingTemplatesQueryKey() });
      trackEvent('naming_template_saved', {
        source,
        ...(source === 'onboarding' ? { onboarding_step: 'naming_template_saved' } : {}),
        token_count: tokens.length,
        is_first_template: !currentTemplate,
      });
      setIsDirty(false);
      setIsEditing(false);
      // Notify parent so it can re-fetch the suggestion from the backend
      onTemplateSaved?.();
      AlertMessage('success', 'Firm default template saved', 2000);
    } catch (err: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      const msg = err?.response?.data?.errors?.join(', ') || 'Failed to save template';
      AlertMessage('error', msg, 3000);
    }
  }, [tokens, separators, saveTemplate, queryClient, onTemplateSaved, trackEvent, source, currentTemplate]);

  const canSave = isEditing && isDirty && !saving && tokens.length > 0;

  // ----- Render -----
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start', pt: '14vh' } }}
      PaperProps={{ sx: { borderRadius: '8px', overflow: 'hidden', maxHeight: 700 } }}>

      {/* Header - pale blue-gray band with AI icon, title/subtitle, firm-wide badge */}
      <Box sx={{ backgroundColor: COLORS.templateModal.headerBg, px: 1.875, py: 1.5625, borderBottom: `1px solid ${COLORS.templateModal.headerBorder}` }}>
        <Stack direction="row" alignItems="flex-start" gap={0.9375}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '8px', flexShrink: 0,
            backgroundColor: COLORS.templateModal.iconBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AutoAwesome sx={{ color: COLORS.templateModal.iconColor, fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" gap={0.9375} flexWrap="wrap">
              <Typography variant="h6" sx={{ color: COLORS.templateModal.sectionTitle, fontWeight: 600, fontSize: '18px', lineHeight: 1.2 }}>
                File naming pattern
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                gap={0.3125}
                sx={{
                  backgroundColor: COLORS.templateModal.badgeBg,
                  border: `1px solid ${COLORS.templateModal.badgeBorder}`,
                  color: COLORS.templateModal.badgeText,
                  borderRadius: '6px', px: 0.625, py: 0.15625,
                }}
              >
                <PeopleAltOutlined sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>Firm-wide setting</Typography>
              </Stack>
            </Stack>
            <Typography variant="body2" sx={{ color: COLORS.templateModal.sectionHelper, mt: 0.3125, fontSize: '13px', lineHeight: 1.45 }}>
              Teach Liscio how your firm names files after AI reads them.
            </Typography>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{
              width: 32, height: 32, flexShrink: 0, p: 0,
              color: '#637381', borderRadius: '8px', mt: -0.15625, mr: -0.3125,
              transition: 'background-color 0.15s ease, color 0.15s ease',
              '&:hover': { backgroundColor: 'rgba(99,115,129,0.1)', color: '#212b36' },
              '&:focus-visible': { outline: 'none', boxShadow: '0 0 0 3px rgba(99,115,129,0.25)' },
            }}
          >
            <Close sx={{ fontSize: 22 }} />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ px: 1.875, py: 2.1875 }}>
        {loading ? (
          <Stack alignItems="center" py={3.75}>
            <CircularProgress size={28} sx={{ color: COLORS.text.secondary }} />
          </Stack>
        ) : (
          <Stack spacing={1.875}>

            {/* ----- Naming pattern editor ----- */}
            <Box>
              <Typography sx={{ color: COLORS.templateModal.sectionTitle, fontWeight: 600, fontSize: '15px', letterSpacing: '-0.01em' }}>
                Naming pattern
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.templateModal.sectionHelper, mt: 0.3125, mb: 1.09375, fontSize: '13px', lineHeight: 1.5 }}>
                Arrange the fields once. Liscio will use this structure for future file name suggestions.
              </Typography>

              {!isEditing ? (
                /* ----- View mode: read-only chips with separator glyphs + edit icon ----- */
                <Box sx={{
                  border: `1px solid ${COLORS.templateModal.editorBorder}`,
                  borderRadius: '8px',
                  px: 0.9375, py: 0.78125, minHeight: 44,
                  backgroundColor: COLORS.templateModal.editorBg,
                  display: 'flex', alignItems: 'center', gap: 0.625,
                }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.46875, flex: 1, alignItems: 'center' }}>
                    {currentParsed.tokens.length > 0 ? (
                      currentParsed.tokens.map((token, i) => (
                        <React.Fragment key={token.id}>
                          <Chip
                            label={getChipLabel(token, allVars, formatHints)}
                            variant="outlined"
                            sx={{
                              height: 30, fontSize: '13px', fontWeight: 500,
                              borderColor: COLORS.chip.view.border, borderRadius: '6px',
                              color: COLORS.chip.view.text, backgroundColor: COLORS.chip.view.bg,
                            }}
                          />
                          {i < currentParsed.tokens.length - 1 && (
                            <SeparatorGlyph value={currentParsed.separators[i] ?? ' '} />
                          )}
                        </React.Fragment>
                      ))
                    ) : (
                      <Typography sx={{ fontSize: '13px', color: COLORS.text.placeholder, fontStyle: 'italic' }}>
                        No template configured
                      </Typography>
                    )}
                  </Box>
                  <Box
                    component="button"
                    onClick={enterEditMode}
                    aria-label="Edit template"
                    sx={{
                      background: 'none', border: 'none', cursor: 'pointer', p: 0.3125,
                      display: 'flex', alignItems: 'center', color: COLORS.text.secondary, flexShrink: 0,
                      borderRadius: 1, '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)', color: COLORS.text.primary },
                    }}
                  >
                    <EditOutlined sx={{ fontSize: '18px' }} />
                  </Box>
                </Box>
              ) : (
                /* ----- Edit mode: editable chips with separator pills - drag to reorder ----- */
                <Box sx={{
                  border: `1px solid ${isDirty ? COLORS.templateModal.editorBorderActive : COLORS.templateModal.editorBorder}`,
                  borderRadius: '8px',
                  px: 0.9375, py: 0.78125, minHeight: 44,
                  backgroundColor: isDirty ? COLORS.templateModal.editorBgActive : COLORS.templateModal.editorBg,
                  display: 'flex', alignItems: 'center', gap: 0.625,
                  transition: 'border-color 0.15s ease, background-color 0.15s ease',
                }}>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={tokenIds} strategy={horizontalListSortingStrategy}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.625, alignItems: 'center', minHeight: 36, flex: 1 }}>
                        {tokens.length === 0 && (
                          <Typography sx={{ fontSize: '13px', color: COLORS.text.placeholder }}>
                            Click a field below to start building your template
                          </Typography>
                        )}
                        {tokens.map((token, i) => (
                          <React.Fragment key={token.id}>
                            <SortableChip
                              id={tokenIds[i]}
                              label={getEditChipLabel(token, allVars, formatHints)}
                              onClickChip={(e) => openPopoverForEdit(i, e.currentTarget)}
                              onRemove={() => removeToken(i)}
                            />
                            {i < tokens.length - 1 && (
                              <SeparatorPill
                                value={separators[i] ?? ' '}
                                onClick={(e) => openSepPopover(i, e.currentTarget)}
                              />
                            )}
                          </React.Fragment>
                        ))}
                      </Box>
                    </SortableContext>
                  </DndContext>
                  {isDirty && (
                    <Box
                      component="button"
                      onClick={() => { setTokens(currentParsed.tokens); setSeparators(currentParsed.separators); setIsDirty(false); }}
                      title="Reset to current template"
                      sx={{
                        background: 'none', border: 'none', cursor: 'pointer', p: 0.3125,
                        display: 'flex', alignItems: 'center', color: COLORS.text.secondary, flexShrink: 0,
                        borderRadius: 1, '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)', color: COLORS.text.primary },
                      }}
                    >
                      <RestartAltOutlined sx={{ fontSize: '18px' }} />
                    </Box>
                  )}
                </Box>
              )}

              {/* Available fields - secondary controls for building the pattern (edit mode) */}
              {isEditing && (
                <Box sx={{ mt: 1.25 }}>
                  <Typography sx={{ color: COLORS.templateModal.availableLabel, fontSize: '13px', fontWeight: 600, mb: 0.625 }}>
                    Available fields
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.46875 }}>
                    {availableVars.map((varName) => (
                      <Chip
                        key={varName}
                        icon={<Add sx={{ fontSize: '14px !important', color: `${COLORS.text.subtle} !important`, ml: '2px !important' }} />}
                        label={`${allVars[varName]?.label || varName}${formatHints[varName] ? ' ▾' : ''}`}
                        onClick={(e) => openPopoverForAdd(varName, e.currentTarget)}
                        variant="outlined"
                        sx={{
                          height: 30, fontSize: '13px', fontWeight: 400, cursor: 'pointer',
                          borderColor: COLORS.chip.available.border, borderRadius: '6px',
                          color: COLORS.chip.available.text, backgroundColor: COLORS.chip.available.bg, pl: 0.15625,
                          '& .MuiChip-icon': { mr: 0.15625 },
                          '&:hover': { backgroundColor: COLORS.surface.hover, borderColor: COLORS.chip.available.borderHover },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            <Divider sx={{ borderColor: COLORS.templateModal.panelBorder, my: 0.3125 }} />

            {/* ----- File name preview ----- */}
            <Box sx={{ pt: 0.3125 }}>
              <Typography sx={{ color: COLORS.templateModal.previewTitle, fontWeight: 600, fontSize: '15px', letterSpacing: '-0.01em', mb: 1.09375 }}>
                File name preview
              </Typography>

              <Box sx={{
                backgroundColor: COLORS.templateModal.panelBg,
                border: `1px solid ${COLORS.templateModal.panelBorder}`,
                borderRadius: '8px',
                p: 1.40625,
                boxShadow: COLORS.templateModal.panelShadow,
              }}>
                {/* Before / after */}
                <Stack direction="row" alignItems="stretch" spacing={1.09375}>
                  <Box sx={{
                    flex: 1, minWidth: 0,
                    backgroundColor: COLORS.templateModal.beforeBg,
                    border: `1px solid ${COLORS.templateModal.beforeBorder}`,
                    borderRadius: '8px', px: 1.09375, py: 0.9375,
                  }}>
                    <Typography sx={{ color: COLORS.templateModal.beforeLabel, fontWeight: 600, fontSize: '11px', letterSpacing: '0.05em', textTransform: 'uppercase', mb: 0.46875 }}>
                      Original file name
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: COLORS.chip.view.text, lineHeight: 1.45, wordBreak: 'break-word' }}>
                      {originalFileName}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, px: 0.15625 }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: '50%',
                      backgroundColor: COLORS.templateModal.arrowBg,
                      border: `1px solid ${COLORS.templateModal.arrowBorder}`,
                      boxShadow: '0 1px 3px rgba(51, 102, 204, 0.12)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <ArrowForward sx={{ fontSize: 18, color: COLORS.templateModal.arrowColor }} />
                    </Box>
                  </Box>

                  <Box sx={{
                    flex: 1.05, minWidth: 0,
                    backgroundColor: COLORS.templateModal.afterBg,
                    border: `2px solid ${COLORS.templateModal.afterBorder}`,
                    borderRadius: '8px', px: 1.09375, py: 0.9375,
                    boxShadow: '0 1px 4px rgba(51, 102, 204, 0.08)',
                  }}>
                    <Typography sx={{ color: COLORS.templateModal.afterLabel, fontWeight: 700, fontSize: '11px', letterSpacing: '0.05em', textTransform: 'uppercase', mb: 0.46875 }}>
                      New file name
                    </Typography>
                    <Typography sx={{ fontSize: '15px', fontWeight: 600, color: newFileName ? COLORS.text.primary : COLORS.text.placeholder, lineHeight: 1.4, wordBreak: 'break-word' }}>
                      {newFileName || 'File name preview'}
                    </Typography>
                  </Box>
                </Stack>

                {/* Fields Liscio found */}
                {detectedFields.length > 0 && (
                  <Box sx={{ mt: 1.40625, pt: 1.25, borderTop: `1px solid ${COLORS.templateModal.beforeBorder}` }}>
                    <Typography sx={{ color: COLORS.text.secondary, fontSize: '13px', fontWeight: 600, mb: 0.78125 }}>
                      Fields Liscio found
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.625}>
                      {detectedFields.map((field) => (
                        <Box
                          key={field.variable}
                          sx={{
                            backgroundColor: COLORS.templateModal.fieldBg,
                            border: `1px solid ${COLORS.templateModal.fieldBorder}`,
                            borderRadius: '8px', px: 0.9375, py: 0.625, textAlign: 'center', minWidth: 72,
                          }}
                        >
                          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: COLORS.text.primary, lineHeight: 1.3 }}>
                            {field.value}
                          </Typography>
                          <Typography sx={{ fontSize: '12px', color: COLORS.templateModal.fieldLabel, mt: 0.21875, lineHeight: 1.2 }}>
                            {field.label}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Format hint popover */}
            <Popper open={!!popoverVar} anchorEl={popoverAnchor} placement="bottom-start" disablePortal sx={{ zIndex: 1400 }}>
              <ClickAwayListener onClickAway={closeAllPopovers} mouseEvent="onMouseDown">
                <Paper elevation={3} sx={{ mt: 0.3125, borderRadius: 2, minWidth: 240, overflow: 'hidden' }}>
                  <Typography variant="caption" sx={{ color: '#919eab', fontWeight: 500, px: 1.25, pt: 0.9375, pb: 0.3125, display: 'block' }}>
                    {editingTokenIndex !== null ? 'Change Format' : 'Select Format'}
                  </Typography>
                  {popoverVar && (
                    <Stack>
                      {formatHints[popoverVar] && Object.entries(formatHints[popoverVar]).map(([hint, meta]) => (
                        <MenuItem
                          key={hint}
                          onClick={() => selectHint(hint)}
                          sx={{ px: 1.25, py: 0.625, display: 'flex', justifyContent: 'space-between', gap: 1.25 }}
                        >
                          <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>{meta.description}</Typography>
                          <Typography sx={{ color: '#919eab', fontFamily: 'monospace', fontSize: '13px' }}>{meta.example}</Typography>
                        </MenuItem>
                      ))}
                    </Stack>
                  )}
                </Paper>
              </ClickAwayListener>
            </Popper>

            {/* Separator popover */}
            <Popper open={sepPopoverIndex !== null} anchorEl={sepPopoverAnchor} placement="bottom-start" disablePortal sx={{ zIndex: 1400 }}>
              <ClickAwayListener onClickAway={closeAllPopovers} mouseEvent="onMouseDown">
                <Paper elevation={3} sx={{ mt: 0.3125, borderRadius: 2, minWidth: 160, overflow: 'hidden' }}>
                  <Typography variant="caption" sx={{ color: '#919eab', fontWeight: 500, px: 1.25, pt: 0.9375, pb: 0.3125, display: 'block' }}>
                    Separator
                  </Typography>
                  <Stack>
                    {SEPARATOR_OPTIONS.map((opt) => (
                      <MenuItem
                        key={opt.value}
                        onClick={() => selectSeparator(opt.value)}
                        selected={sepPopoverIndex !== null && separators[sepPopoverIndex] === opt.value}
                        sx={{ px: 1.25, py: 0.46875, display: 'flex', justifyContent: 'space-between', gap: 1.25 }}
                      >
                        <Typography sx={{ fontSize: '14px' }}>{opt.label}</Typography>
                        <Typography sx={{ color: '#919eab', fontFamily: 'monospace', fontSize: '14px' }}>{opt.display}</Typography>
                      </MenuItem>
                    ))}
                  </Stack>
                </Paper>
              </ClickAwayListener>
            </Popper>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 1.875, py: 1.40625, borderTop: `1px solid ${COLORS.surface.border}`, justifyContent: 'space-between' }}>
        <Typography variant="body2" sx={{ color: COLORS.templateModal.footerHelper, fontSize: '13px', lineHeight: 1.45, maxWidth: '55%' }}>
          You can update this anytime from File Intelligence settings.
        </Typography>
        <Stack direction="row" spacing={0.625}>
          <Button onClick={onClose} sx={{ color: COLORS.text.secondary, textTransform: 'none' }}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!canSave}
            sx={{ backgroundColor: COLORS.modal.save, textTransform: 'none', px: 1.875, '&:hover': { backgroundColor: COLORS.modal.saveHover } }}
          >
            {saving ? 'Saving...' : 'Save pattern'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
